import qs from 'qs'

import { PaginatedApiClient } from 'api/PaginatedApiClient'
import { ArticleFlatData, sendToApi } from 'api/client'
import { Article as CoreArticle } from 'common/ArticleAndCreatorCard/ArticleAndCreatorCard'
import { getHandbookPassword } from 'hooks/useHandbookPasswords'
import { type CoreArticleCollections } from 'hooks/useCoreArticleCollection/constants'
import store from 'redux/store'
import { TimelineEvent as ActivityTimelineEvent } from 'screens/Activity/Types'
import {
  ArticleFlat,
  ArticleStyle,
  Comment,
  CommentUser,
  Doc,
  GroupLayerAccessGrant,
  HandbookSubDoc,
  HandbookV2,
  Layer,
  LayerSummary,
  NavSortOrder,
  UserLayerAccessGrant,
  Workspace,
} from 'types'

import { AfterMergeAction } from '../../components/CompleteMergeModal/types'
import { CloseReviewActionConfig } from '../../stores/Workflows/Types'

export type ArticleFlatsData = { articles: ArticleFlat[] }

export interface EventUser {
  id: number
  display_name: string
  avatar_url: string | null
}

export interface FeedbackRequestStartedEvent extends TimelineEvent {
  type: 'Event::ArticleReadReceiptRequest'
  user: EventUser
  data: {
    requestees: EventUser[]
    feedback_request: {
      id: string
      created_at: string
    }
  }
}

export interface FeedbackRequestEndedEvent extends TimelineEvent {
  type: 'Event::ArticleEndReadReceipt'
  user: EventUser
  data: {
    completed_requestees: EventUser[]
    feedback_request: {
      id: string
      ended_at: string
    }
  }
}

export interface ReviewApproveEvent extends TimelineEvent {
  type: 'Event::ArticleReviewApproval'
  user: EventUser
  data: {
    reviewer: EventUser
    user_review: {
      message: string
    }
  }
}

export interface ReviewCancelEvent extends TimelineEvent {
  type: 'Event::ArticleReviewCancel'
  user: EventUser
  data: {
    reviewers: EventUser[]
  }
}

export interface ReviewRejectEvent extends TimelineEvent {
  type: 'Event::ArticleReviewRejection'
  user: EventUser
  data: {
    reviewer: EventUser
    message: string
  }
}

export interface ReviewReminderEvent extends TimelineEvent {
  type: 'Event::ArticleReviewReminder'
  user: EventUser
  data: {
    user_review: {
      id: number
      reviewer: {
        id: number
        display_name: string
      }
    }
  }
}

export interface SubmitChangesSuggestionEvent extends TimelineEvent {
  type: 'Event::SubmitChangesSuggestion'
  user: EventUser
  data: {
    branch_url?: string
  }
}

export interface MergeChangesSuggestionEvent extends TimelineEvent {
  type: 'Event::MergeChangesSuggestion'
  user: EventUser
  data: {
    branch_url: string | null
    description: string | null
  }
}

export interface AssignTodoEvent extends TimelineEvent {
  type: 'Event::AssignTodo'
  user: EventUser
  data: {
    assignee: Partial<EventUser>
    todo_id: number
  }
}

export interface CompleteTodoEvent extends TimelineEvent {
  type: 'Event::CompleteTodo'
  user: EventUser
  data: {
    todo_id: number
  }
}

export interface CommentGroupReply extends Comment {
  comment: string
  comment_id: number
  timestamp: string
  user: CommentUser
  created_in_time_range: boolean
}

export interface CommentGroupComment extends Comment {
  id: number
  created_in_time_range: boolean
  timestamp: string
  comment_id: number
  user: CommentUser
  replies: CommentGroupReply[]
}

export interface CommentEvent extends TimelineEvent {
  type: 'CommentsGroup'
  data: CommentGroupComment[]
  commenters: number
}

export interface RepliesGroup extends TimelineEvent {
  type: 'RepliesGroup'
  data: CommentGroupComment[]
  commenters: number
}

export interface ReviewRequestEvent extends TimelineEvent {
  type: 'Event::ArticleReviewRequest'
  user: EventUser
  data: {
    reviewer: EventUser
    user_review: {
      id: number
      message: string
      subject: string
    }
  }
}

export interface ArticleSuggestionReviewsCreatedEvent extends TimelineEvent {
  type: 'Event::ArticleSuggestionReviewsCreated'
  user: EventUser
  data: {
    branch_url: string
    iteration: number
    message: string
  }
}

export interface ArticleSuggestionReminder extends TimelineEvent {
  type: 'Event::ArticleSuggestionReminder'
  user: EventUser
  data: {
    suggestion: {
      id: number
      suggester_id: number
    }
    recipient: {
      id: number
      display_name: string
    }
  }
}

export interface ArticleSuggestionReviewRejected extends TimelineEvent {
  type: 'Event::ArticleSuggestionReviewRejected'
  user: EventUser
  data: {
    suggester: {
      avatar_url: string
      display_name: string
      id: number
    }
    message: string
  }
}

export interface ArticleSuggestionApprovedAndMerged extends TimelineEvent {
  type: 'Event::ArticleSuggestionApprovedAndMerged'
  user: EventUser
  data: {
    message: string
    suggester: {
      id: number
      display_name: string
    }
  }
}

export interface ArticleSuggestionCancelled extends TimelineEvent {
  type: 'Event::ArticleSuggestionCancelled'
  user: EventUser
  data: {
    suggester: {
      id: number
      display_name: string
    }
  }
}

export interface ArticleInformalFeedbackReviewsCreated extends TimelineEvent {
  type: 'Event::ArticleInformalFeedbackReviewsCreated'
  user: EventUser
  data: {
    request_id: number
    requester: {
      display_name: string
    }
    reviews: Array<{ id: number }>
    message: string
  }
}

export interface ArticleInformalFeedbackReviewSubmitted extends TimelineEvent {
  type: 'Event::ArticleInformalFeedbackReviewSubmitted'
  user: EventUser
  data: {
    reviewer: {
      display_name: string
    }
    requester: {
      display_name: string
    }
    request_id: number
    message: string
  }
}

export interface ArticleInformalFeedbackRequestClosed extends TimelineEvent {
  type: 'Event::ArticleInformalFeedbackRequestClosed'
  user: EventUser
  data: {
    initiator: {
      display_name: string
    }
    request_id: number
    message: string
  }
}

export interface ArticleAnalytics {
  total_unresolved_comments: number
  total_edits: number
  total_incomplete_todos: number
  total_users: number
  total_views: number
}

export interface ArticleUserAnalytics {
  user_avatar: string | null
  user_name: string
  user_incomplete_todos: number
  user_last_edited: string | null
  user_last_viewed: string | null
  user_unresolved_comments: number
}

export interface TimelineEvent {
  id: string
  data: any
  timestamp: string
  user_count?: number
  last_viewed: boolean
  last_timestamp?: string
  name: string | null
  clickable?: boolean
  version: number
  article: {
    key: string
    slug: string
    title: string
    header_emoji?: string | null
  }
  layer: LayerSummary
  type:
    | 'CommentsGroup'
    | 'EditsGroup'
    | 'Event::ArticleCopy'
    | 'Event::ArticleEndReadReceipt'
    | 'Event::ArticleMerged'
    | 'Event::ArticleMergedInto'
    | 'Event::ArticleReadReceiptRequest'
    | 'Event::ArticleReviewApproval'
    | 'Event::ArticleReviewCancel'
    | 'Event::ArticleReviewRejection'
    | 'Event::ArticleReviewRequest'
    | 'Event::ArticleReviewReminder'
    | 'Event::AssignTodo'
    | 'Event::CompleteTodo'
    | 'Event::GrantArticleAccess'
    | 'Event::MergeChangesSuggestion'
    | 'Event::SubmitChangesSuggestion'
    | 'RepliesGroup'
    | 'Event::ArticleSuggestionReviewsCreated'
    | 'Event::ArticleSuggestionReminder'
    | 'Event::ArticleSuggestionCancelled'
    | 'Event::ArticleSuggestionReviewRejected'
    | 'Event::ArticleSuggestionApprovedAndMerged'
    | 'Event::ArticleInformalFeedbackReviewsCreated'
    | 'Event::ArticleInformalFeedbackReviewSubmitted'
    | 'Event::ArticleInformalFeedbackReviewDismissed'
    | 'Event::ArticleInformalFeedbackRequestClosed'
}

export type Timeline = {
  created_at: string
  creator: {
    avatar_url: string
    company: any
    display_name: string
    first_name: string
    id: number
    last_name: string | null
    seo_string: string
  }
  events: TimelineEvent[]
  oldest_timestamp: string
  more_available: boolean
}

export enum TimelineEventFilter {
  Merge = 'merge',
  Copy = 'copy',
  Edit = 'edit',
  AI = 'ai',
}

interface RevisionDoc {
  type: 'doc'
  content: Element[]
}

export interface Revision {
  changeset: any
  baseElements: RevisionDoc
  revisionElements: RevisionDoc
  fromDoc: RevisionDoc
  toDoc: RevisionDoc
  combinedDoc: RevisionDoc
}

export interface CoreArticleCollection {
  title: string
  sub_header: string
  articles: CoreArticle[]
}

export const ArticleClient = {
  getArticle: async (
    id: string,
    options?: {
      withExtraSteps?: boolean
      withAccessMap?: boolean
      withAncestors?: boolean
      enteredPassword?: string | null
      handbookKey?: string
      withArticleView?: boolean
    },
  ) => {
    const params: {
      preview: boolean
      t?: string
      snapshot: boolean
      extra_steps?: boolean
      with_access_map?: boolean
      with_ancestors?: boolean
      entered_password?: string
      handbook_key?: string
      with_article_view?: boolean
    } = {
      preview: true,
      snapshot: true,
    }
    const existingPasswords = store.getState().articlePassword

    const handbookPassword = getHandbookPassword(store.getState().handbook?.key) || undefined

    if (options?.withExtraSteps) params.extra_steps = true
    if (options?.withAccessMap) params.with_access_map = true
    if (options?.withAncestors) params.with_ancestors = true
    if (options?.enteredPassword) params.entered_password = options.enteredPassword
    if (options?.handbookKey) params.handbook_key = options.handbookKey
    if (existingPasswords[id]) params.entered_password = existingPasswords[id] || handbookPassword
    if (options?.withArticleView) params.with_article_view = true

    return sendToApi<ArticleFlatData>(`/articles/${id}`, {
      params: { ...params, include: ['almanac_file.version_control_mode.metadata'] },
    })
  },

  publishArticle: async (articleId: string, subscriptionIds?: number[]) => {
    return sendToApi<ArticleFlatData>(`/articles/${articleId}/publish`, {
      method: 'POST',
      body: { subscription_ids: subscriptionIds },
    })
  },

  checkPassword: async (articleId: string, password: string) => {
    return sendToApi<void>(`/articles/${articleId}/check_password`, {
      method: 'POST',
      body: { entered_password: password },
    })
  },

  getLastUserArticle: async (userId: number, workspaceId: number) => {
    return sendToApi<{ article: ArticleFlat }>(`/users/${userId}/last_article?workspace_id=${workspaceId}`)
  },

  getUserArticles: async (userId: number) => {
    return sendToApi<ArticleFlatsData>(`/users/${userId}/articles_public`)
  },

  deepCopy: (articleId: string, workspaceKey: string, copyTitle?: string) => {
    return sendToApi<ArticleFlatData>(`/articles/${articleId}/deep_copy`, {
      method: 'POST',
      body: {
        workspace_id: workspaceKey,
        copy_title: copyTitle,
      },
    })
  },

  getArticleAsMarkdown: async (articleId: string) => {
    return sendToApi<{ markdown: string }>(`/articles/${articleId}/markdown`)
  },

  getTimelineEvents: async (
    articleId: string,
    timestamp?: string,
    userId?: string | null,
    withChanges: boolean = false,
    layerId?: string,
    types?: TimelineEventFilter[],
  ) => {
    return sendToApi<{ timeline: Timeline }>(`/articles/${articleId}/timeline`, {
      params: { timestamp, user_id: userId, with_changes: withChanges, layer_id: layerId, types },
      stringify: (object) => qs.stringify(object, { arrayFormat: 'brackets' }),
    })
  },

  getAnalytics: async (articleId: string) => {
    return sendToApi<{ analytics: ArticleAnalytics }>(`/articles/${articleId}/analytics`)
  },

  getWorkspace: async (articleId: string) => {
    return sendToApi<{ company: Pick<Workspace, 'id' | 'key'> }>(`/articles/${articleId}/workspace`)
  },

  getUserAnalytics: async (articleId: string) => {
    return sendToApi<{ user_analytics: ArticleUserAnalytics[] }>(`/articles/${articleId}/user_analytics`)
  },

  getTimelineEvent: async (articleKey: string, eventId?: string, userId?: string | null) => {
    return sendToApi<{ event: ActivityTimelineEvent }>(`/articles/${articleKey}/timeline_event`, {
      params: { event_id: eventId, user_id: userId },
    })
  },

  mergeArticle: async (
    id: string,
    articleMergedId: string,
    version: number,
    step: any,
    clientUuid: string,
    afterMergeAction: AfterMergeAction,
    acceptedChangesCount?: number,
    rejectedChangesCount?: number,
    generalComment?: string,
    newDoc?: {
      title: string
      companyId: number
      projectId?: number
    },
  ) => {
    return sendToApi(`/articles/${id}/merge`, {
      method: 'POST',
      body: {
        version,
        step,
        client_uuid: clientUuid,
        article_merged_id: articleMergedId,
        accepted_changes_count: acceptedChangesCount,
        rejected_changes_count: rejectedChangesCount,
        general_comment: generalComment,
        after_merge_action: afterMergeAction,
        new_doc_title: newDoc?.title,
        new_doc_company_id: newDoc?.companyId,
        new_doc_project_id: newDoc?.projectId,
      },
    })
  },

  updateArticle: async (id: string, body: {}, clientUuid?: string) => {
    return sendToApi<ArticleFlatData>(`/articles/${id}`, {
      method: 'PUT',
      body: { article: body, client_uuid: clientUuid },
      params: { include: ['almanac_file.version_control_mode.metadata'] },
    })
  },

  dismissLivingDocModal: async (id: string) => {
    return sendToApi(`/articles/${id}/dismiss_living_modal`, {
      method: 'POST',
    })
  },

  fetchSmartLinkTitles: async (DocKeys: string[]) => {
    const searchParams = new URLSearchParams()
    for (const key of DocKeys || []) {
      searchParams.append('keys[]', key)
    }

    return sendToApi<{ [key: string]: string }>(`/articles/titles_from_keys?${searchParams.toString()}`)
  },

  fetchFoldersAndFiles: (workspaceId: number) => {
    return ({ pageParam = 1 }) => {
      return sendToApi(
        `/w/${workspaceId}/all_workspace_docs?sort_order=${NavSortOrder.NameAsc}&page=${pageParam}&items=20`,
      )
    }
  },

  getRecentlyReadArticles: async (articleID: string) => {
    return sendToApi(`/articles/recently_read?current_article_id=${articleID}`)
  },

  getTaskDocs: (workspaceId: number) => new PaginatedApiClient<{ articles: Doc[] }>(`/w/${workspaceId}/task_docs`),
  getFollowUpArticles: (workspaceId: number) =>
    new PaginatedApiClient<{ articles: Doc[] }>(`/w/${workspaceId}/follow_up_articles`),
  getCompletedArticles: (workspaceId: number) =>
    new PaginatedApiClient<{ articles: Doc[] }>(`/w/${workspaceId}/completed_articles`),
  getAccessesForShare: async (articleId: string, handbook: HandbookV2 | undefined = undefined) => {
    return sendToApi<any>(`/articles/${articleId}/accesses/all${handbook ? '?handbook=' + handbook.key : ''}`)
  },
  getHandbookSubdocAccesses: (key: string) =>
    sendToApi<{ edit_articles: HandbookSubDoc[]; non_edit_articles: HandbookSubDoc[] }>(
      `/articles/${key}/subdoc_accesses`,
    ),
  getCustomArticle: (host: string, slug: string) =>
    sendToApi(`/articles/custom_path_article`, {
      params: { slug, host },
    }),
  getRevision: (key: string, from: number, to: number) =>
    sendToApi<{ article_style: ArticleStyle | null; revision: Revision; workspace_article_style: ArticleStyle | null }>(
      `/articles/${key}/revisions/${from}..${to}`,
      {
        method: 'GET',
      },
    ),
  restoreEvent: (key: string, id: string) =>
    sendToApi<{}>(`/articles/${key}/events/${id}/restore`, {
      method: 'POST',
    }),
  updateEvent: (key: string, id: string, attributes: { name: string | null }) =>
    sendToApi<{}>(`/articles/${key}/events/${id}`, {
      method: 'PUT',
      body: {
        attributes: {
          ...attributes,
          name: attributes.name || '',
        },
      },
    }),
  transferOwnership: (key: string, user_id: number) =>
    sendToApi<{}>(`/articles/${key}/transfers`, {
      method: 'POST',
      body: {
        user_id,
      },
    }),
  getLayers: (key: string, articlePassword: string | null = null) =>
    sendToApi<{ layers: Layer[] }>(`/articles/${key}/layers`, {
      method: 'GET',
      params: {
        password: articlePassword,
      },
    }),
  deleteLayer: (articleKey: string, id: string, payload?: Partial<CloseReviewActionConfig>) =>
    sendToApi<{}>(`/articles/${articleKey}/layers/${id}`, {
      method: 'DELETE',
      body: {
        message: payload?.message,
      },
    }),
  getLayer: (articleKey: string, id: string, articlePassword: string | null = null) =>
    sendToApi<{ layer: Layer }>(`/articles/${articleKey}/layers/${id}`, {
      method: 'GET',
      params: {
        password: articlePassword,
      },
    }),
  getCoreArticleCollection: (collectionType: CoreArticleCollections) =>
    sendToApi<{ core_article_collection: CoreArticleCollection }>(`/core_article_collections/${collectionType}`, {
      method: 'GET',
    }),
  createLayerAccesses: (
    articleKey: string,
    layerId: string,
    userAccesses: Array<{ email_address?: string; id?: number }>,
    guestAccesses: Array<{ email_address: string }>,
    groupAccesses: Array<{ id: number }>,
  ) =>
    sendToApi<{
      user_accesses: Array<{ errors: string[]; access: UserLayerAccessGrant }>
      guest_accesses: Array<{ errors: string[]; access: UserLayerAccessGrant }>
      group_accesses: Array<{ errors: string[]; access: GroupLayerAccessGrant }>
    }>(`/articles/${articleKey}/layers/${layerId}/accesses`, {
      method: 'POST',
      body: {
        user_accesses: userAccesses,
        guest_accesses: guestAccesses,
        group_accesses: groupAccesses,
      },
    }),

  trackCopyUsage: (articleKey: string, sourceArticleKey: string) =>
    sendToApi(`/articles/${articleKey}/track_copy_usage`, {
      method: 'POST',
      body: {
        source_id: sourceArticleKey,
      },
    }),
  trackArticleUpdate: (articleKey: string) =>
    sendToApi(`/articles/${articleKey}/track_article_update`, {
      method: 'POST',
    }),

  rateGeneration: (generationId: number, rating: 'downvote' | 'upvote') =>
    sendToApi(`/rate_generation`, {
      method: 'POST',
      body: { ai_suggestion_request_id: generationId, rating },
    }),
}
